import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  addDoc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../../services/firebase";
import CustomersTab from "./datahub/CustomersTab";
import TrucksTab from "./datahub/TrucksTab";
import BinsTab from "./datahub/BinsTab";
import JobTypesTab from "./datahub/JobTypesTab";
import DriverCompaniesTab from "./datahub/DriverCompaniesTab";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeEmailList(values = []) {
  const list = Array.isArray(values)
    ? values
    : String(values || "").split(/[;,\n]+/);
  const seen = new Set();

  return list
    .map((value) => String(value || "").trim().toLowerCase())
    .filter((email) => {
      if (!email || !EMAIL_RE.test(email) || seen.has(email)) return false;
      seen.add(email);
      return true;
    });
}

function invalidEmails(values = []) {
  const list = Array.isArray(values)
    ? values
    : String(values || "").split(/[;,\n]+/);

  return list
    .map((value) => String(value || "").trim().toLowerCase())
    .filter((email) => email && !EMAIL_RE.test(email));
}

export default function AdminDataHub() {
  const [companies, setCompanies] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [newCompany, setNewCompany] = useState({
    name: "",
    uiVariant: "default",
    managerEmailInput: "",
  });
  const [tab, setTab] = useState("customers");

  // Edit modal state
  const [showEdit, setShowEdit] = useState(false);
  const [editCompany, setEditCompany] = useState({
    name: "",
    uiVariant: "default",
    managerEmails: [],
    managerEmailInput: "",
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "companies"), (snap) => {
      const rows = snap.docs
        .map((d) => {
          const data = d.data() || {};
          const rawName =
            data.name ||
            data.companyName ||
            data.displayName ||
            "";
          const name = String(rawName).trim();
          return {
            id: d.id,
            ...data,
            name: name || "Untitled company",
          };
        })
        .filter((c) => c.id)
        .sort((a, b) => a.name.localeCompare(b.name));

      setCompanies(rows);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!companies.length) {
      if (selectedId) setSelectedId("");
      return;
    }

    if (!selectedId || !companies.some((c) => c.id === selectedId)) {
      setSelectedId(companies[0].id);
    }
  }, [companies, selectedId]);

  const company = useMemo(
    () => companies.find((c) => c.id === selectedId) || null,
    [companies, selectedId]
  );

  async function createCompany(e) {
    e.preventDefault();
    const name = newCompany.name.trim();
    if (!name) return;
    const invalidManagers = invalidEmails(newCompany.managerEmailInput);
    if (invalidManagers.length) {
      alert(`Please fix manager email: ${invalidManagers[0]}`);
      return;
    }

    const ref = await addDoc(collection(db, "companies"), {
      name,
      nameLower: name.toLowerCase(),
      uiVariant: newCompany.uiVariant || "default",
      managerEmails: normalizeEmailList(newCompany.managerEmailInput),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    setNewCompany({ name: "", uiVariant: "default", managerEmailInput: "" });
    setSelectedId(ref.id);
  }

  function openEditor() {
    if (!company) return;
    setEditCompany({
      name: company.name || "",
      uiVariant: company.uiVariant || "default",
      managerEmails: normalizeEmailList(company.managerEmails || company.managerEmail || ""),
      managerEmailInput: "",
    });
    setShowEdit(true);
  }

  async function saveEdits(e) {
    e?.preventDefault();
    if (!selectedId) return;

    const name = editCompany.name.trim();
    if (!name) return;
    const invalidManagers = invalidEmails(editCompany.managerEmails);
    if (invalidManagers.length) {
      alert(`Please fix manager email: ${invalidManagers[0]}`);
      return;
    }

    setSaving(true);
    try {
      const managerEmails = normalizeEmailList(editCompany.managerEmails);
      await updateDoc(doc(db, "companies", selectedId), {
        name,
        nameLower: name.toLowerCase(),
        uiVariant: editCompany.uiVariant || "default",
        managerEmails,
        updatedAt: serverTimestamp(),
      });
      setShowEdit(false);
    } finally {
      setSaving(false);
    }
  }

  async function deleteCurrentCompany() {
    if (!selectedId) return;
    const confirmed = window.confirm(
      "Delete this company? This removes the company document immediately. Any sub-collections are not deleted automatically."
    );
    if (!confirmed) return;

    setDeleting(true);
    try {
      await deleteDoc(doc(db, "companies", selectedId));
      setShowEdit(false);
      setSelectedId("");
    } finally {
      setDeleting(false);
    }
  }

  const tabs = [
    { key: "customers", label: "Customers" },
    { key: "trucks", label: "Trucks" },
    { key: "bins", label: "Bins" },
    { key: "jobtypes", label: "Services" },
    { key: "drivers", label: "Drivers" },
  ];

  const companyManagerEmails = normalizeEmailList(company?.managerEmails || company?.managerEmail || "");

  return (
    <div className="w-full px-4 py-4 sm:px-6 sm:py-6 xl:px-8 space-y-6 text-gray-900">
      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex items-baseline gap-2.5">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-600">
            Admin
          </span>
          <h1 className="text-lg font-semibold tracking-tight text-gray-950">Data hub</h1>
        </div>
        <div className="mt-1 text-xs text-gray-600">
          <span className="font-semibold text-gray-900">{companies.length}</span> companies
          {" · "}
          {company ? (
            <>
              <span className="font-semibold text-gray-900">{company.name}</span> ({company.uiVariant || "default"})
            </>
          ) : (
            "No company selected"
          )}
        </div>
      </div>

      <div className="rounded-[24px] border border-gray-200 bg-white p-4 shadow-sm space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          {/* Company selector */}
          <label className="block flex-1 sm:max-w-sm">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Company
            </span>
            <div className="mt-1 flex items-center gap-2">
              <select
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
              >
                {companies.length === 0 && (
                  <option value="">No companies yet</option>
                )}
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={openEditor}
                className="whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-black transition disabled:opacity-50"
                disabled={!company}
                title="Edit the selected company"
              >
                Edit
              </button>
            </div>
          </label>

          <button
            type="button"
            onClick={() => setManageOpen((v) => !v)}
            aria-expanded={manageOpen}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition"
          >
            {manageOpen ? "Hide" : "Add company / manager emails"}
            <svg
              className={`h-3.5 w-3.5 text-gray-400 transition-transform ${manageOpen ? "rotate-180" : ""}`}
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
        </div>

        {manageOpen && (
        <div className="space-y-4 border-t border-gray-100 pt-4">
          {/* Quick add */}
          <form
            className="block space-y-1.5"
            onSubmit={createCompany}
          >
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Quick add company
            </span>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
              <input
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400 sm:col-span-3"
                placeholder="Company name"
                value={newCompany.name}
                onChange={(e) =>
                  setNewCompany((p) => ({ ...p, name: e.target.value }))
                }
              />
              <select
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
                value={newCompany.uiVariant}
                onChange={(e) =>
                  setNewCompany((p) => ({ ...p, uiVariant: e.target.value }))
                }
                title="Greenspace pages can render differently per company"
              >
                <option value="default">Default</option>
                <option value="greenspace">Greenspace</option>
              </select>
              <button
                className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 transition disabled:opacity-60"
                type="submit"
                disabled={!newCompany.name.trim()}
              >
                Add
              </button>
            </div>
            <input
              className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
              placeholder="Optional manager emails, separated by comma or newline"
              value={newCompany.managerEmailInput}
              onChange={(e) =>
                setNewCompany((p) => ({ ...p, managerEmailInput: e.target.value }))
              }
            />
          </form>

          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Manager notification emails
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {!company ? (
                <span className="text-sm text-gray-500">Select a company.</span>
              ) : companyManagerEmails.length ? (
                companyManagerEmails.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-xs font-medium text-emerald-800"
                  >
                    {email}
                  </span>
                ))
              ) : (
                <span className="text-sm text-gray-500">
                  No company manager emails. Global manager email will be used as fallback.
                </span>
              )}
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Tabs + content */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-gray-200 px-3 py-2">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                tab === t.key
                  ? "bg-gray-900 text-white shadow-sm"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="p-4">
          {!company ? (
            <div className="text-sm text-gray-600">
              Select or add a company to manage its data.
            </div>
          ) : tab === "customers" ? (
            <CustomersTab key={`customers-${company.id}`} company={company} />
          ) : tab === "trucks" ? (
            <TrucksTab key={`trucks-${company.id}`} company={company} />
          ) : tab === "bins" ? (
            <BinsTab key={`bins-${company.id}`} company={company} />
          ) : tab === "drivers" ? (
            <DriverCompaniesTab
              key={`drivers-${company.id}`}
              companies={companies}
              selectedCompanyId={company.id}
            />
          ) : (
            <JobTypesTab key={`jobtypes-${company.id}`} company={company} />
          )}
        </div>
      </div>

      {/* Edit / Delete modal */}
      {showEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => !saving && !deleting && setShowEdit(false)}
          />

          {/* Modal */}
          <form
            onSubmit={saveEdits}
            className="relative z-10 w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl space-y-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Edit company
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Update the display name and UI variant for this company.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <label className="block">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Name
                </span>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
                  value={editCompany.name}
                  onChange={(e) =>
                    setEditCompany((p) => ({ ...p, name: e.target.value }))
                  }
                  autoFocus
                />
              </label>

              <label className="block">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  UI variant
                </span>
                <select
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
                  value={editCompany.uiVariant}
                  onChange={(e) =>
                    setEditCompany((p) => ({
                      ...p,
                      uiVariant: e.target.value,
                    }))
                  }
                >
                  <option value="default">Default</option>
                  <option value="greenspace">Greenspace</option>
                </select>
              </label>

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Manager emails
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {editCompany.managerEmails.length ? (
                    editCompany.managerEmails.map((email) => (
                      <span
                        key={email}
                        className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-xs font-medium text-emerald-800"
                      >
                        {email}
                        <button
                          type="button"
                          className="text-emerald-900 hover:text-red-600"
                          onClick={() =>
                            setEditCompany((p) => ({
                              ...p,
                              managerEmails: p.managerEmails.filter((item) => item !== email),
                            }))
                          }
                          aria-label={`Remove ${email}`}
                        >
                          x
                        </button>
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500">
                      No company manager emails yet.
                    </span>
                  )}
                </div>
                <div className="mt-3 flex gap-2">
                  <input
                    type="email"
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
                    placeholder="manager@example.com"
                    value={editCompany.managerEmailInput}
                    onChange={(e) =>
                      setEditCompany((p) => ({ ...p, managerEmailInput: e.target.value }))
                    }
                  />
                  <button
                    type="button"
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                    onClick={() => {
                      const invalidManagers = invalidEmails(editCompany.managerEmailInput);
                      if (invalidManagers.length) {
                        alert(`Please enter a valid email address: ${invalidManagers[0]}`);
                        return;
                      }
                      const next = normalizeEmailList([
                        ...editCompany.managerEmails,
                        editCompany.managerEmailInput,
                      ]);
                      if (next.length === editCompany.managerEmails.length) return;
                      setEditCompany((p) => ({
                        ...p,
                        managerEmails: next,
                        managerEmailInput: "",
                      }));
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 pt-2">
              <button
                type="button"
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-60"
                onClick={() => setShowEdit(false)}
                disabled={saving || deleting}
              >
                Cancel
              </button>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={deleteCurrentCompany}
                  className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 transition disabled:opacity-60"
                  disabled={deleting}
                  title="Permanently delete this company"
                >
                  {deleting ? "Deleting..." : "Delete"}
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 transition disabled:opacity-60"
                  disabled={saving || !editCompany.name.trim()}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>

            <p className="text-[11px] text-gray-500 pt-1">
              Deleting removes only the company document. Any related data in
              subcollections (for example customers, bins, or services) is not
              deleted automatically.
            </p>
          </form>
        </div>
      )}
    </div>
  );
}
